import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import type { DataTableFilter } from "~components/DataTable/types/dataTableFilter.ts";
import { MultipleSelectFilter } from "~components/MultipleSelectFilter/MultipleSelectFilter.tsx";
import { TableFilter } from "~enums/tableFilter.ts";
import type { UserRoles } from "~enums/userRoles";
import type { PermissionProducts } from "~pages/Users/enums/permissionProducts.ts";
import { UserPermissionsFilterTypes } from "~pages/Users/enums/userPermissionsFilterTypes.ts";
import useGetUserRoleSelectOptionsByProduct from "~pages/Users/hooks/useGetUserRoleSelectOptionsByProduct.ts";

interface UseGetUserPermissionsTableFiltersProps {
  currentProductTab: PermissionProducts;
  initialUserRoles?: UserRoles[];
}

export const useGetUserPermissionsTableFilters = ({
  currentProductTab,
  initialUserRoles,
}: UseGetUserPermissionsTableFiltersProps): {
  filters: DataTableFilter[];
  currentFilter: Record<string, any>;
  handleFilterChange: (updatedFilter: Record<string, any>) => void;
  resetFilters: () => void;
} => {
  const { formatMessage } = useIntl();
  const [currentFilter, setCurrentFilter] = useState<Record<string, any>>(
    initialUserRoles?.length ? { [UserPermissionsFilterTypes.UserRoles]: initialUserRoles } : {},
  );
  const userRoleOptionsByProduct = useGetUserRoleSelectOptionsByProduct();

  const userRoleFilterOptions = (userRoleOptionsByProduct[currentProductTab] || []).map((opt: any) => ({
    label: opt.label,
    value: opt.value,
  }));

  const handleFilterChange = useCallback((updatedFilter: Record<string, any>) => {
    setCurrentFilter(updatedFilter);
  }, []);

  const resetFilters = useCallback(() => {
    setCurrentFilter({});
  }, []);

  const filters: DataTableFilter[] = [
    {
      filterKey: UserPermissionsFilterTypes.UserRoles,
      tableFilterType: TableFilter.Primary,
      filterElement: (props: { value: any; onChange: (value: any) => void }) => (
        <MultipleSelectFilter
          value={props.value ?? []}
          placeholder={formatMessage({ id: "permissions.user.role.type" })}
          options={userRoleFilterOptions}
          onChange={props.onChange}
        />
      ),
    },
  ];

  return { filters, currentFilter, handleFilterChange, resetFilters };
};
